import React, {
  useRef,
  useState,
  useEffect,
  RefObject,
  forwardRef,
  useImperativeHandle,
  ForwardRefRenderFunction,
} from 'react';
import { Grid } from '@mui/material';

import { transformDSPMetadataToLeft } from 'components/AlarmManagement/components/QueryBuilder/helper/index';
import {
  ValueLabel,
  ValueLabelGroup,
  DataSourceAlarmMetadata,
  AlarmDefinition,
} from 'types/types';

import VirtualizedCollapsibeList, {
  ListRef,
} from './VirtualizedCollapsibeList';

import { Ref as AlarmManagementFormRulesRef } from '../FormRules/FormRules';

interface Props {
  alarmDefinitionData: AlarmDefinition | null;
  dataSourceMetadataForAlarm?: DataSourceAlarmMetadata;
  alarmManagementFormRulesRef: React.MutableRefObject<AlarmManagementFormRulesRef | null>;
}

export interface Ref {
  leftListRef: RefObject<ListRef>;
  rightListRef: RefObject<ListRef>;
}

const VirtualizedTransferList: ForwardRefRenderFunction<Ref, Props> = (
  {
    alarmDefinitionData,
    dataSourceMetadataForAlarm,
    alarmManagementFormRulesRef,
  },
  ref
) => {
  useEffect(() => {
    if (dataSourceMetadataForAlarm) {
      let result: ValueLabelGroup[] = [];
      result = transformDSPMetadataToLeft(dataSourceMetadataForAlarm);

      leftListRef.current?.handleChangeItems(result);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSourceMetadataForAlarm]);

  const leftListRef = useRef<ListRef>(null);
  const rightListRef = useRef<ListRef>(null);

  const handleCheckedRight = (group: string, item: ValueLabel) => {
    if (item.enableDeleteButton) {
      item.enableDeleteButton = false;
      const leftItems = leftListRef.current?.items ?? [];
      let rightItems = rightListRef.current?.items ?? [];

      const rightGroupIndex = rightItems.findIndex((el) => el.group === group);
      if (rightGroupIndex > -1) {
        rightItems[rightGroupIndex].items = rightItems[
          rightGroupIndex
        ]?.items.filter((i) => i.value !== item.value);

        if (rightItems[rightGroupIndex].items.length === 0) {
          rightItems.splice(rightGroupIndex, 1);
        }
      }

      const leftGroupIndex = leftItems?.findIndex((i) => i.group == group);
      if (leftGroupIndex > -1) {
        leftItems[leftGroupIndex].items = [
          ...leftItems[leftGroupIndex].items,
          item,
        ].sort((a, b) => a.index - b.index);
      } else {
        leftItems.push({
          group: group,
          items: [item],
        });
      }

      leftListRef.current?.handleChangeItems(leftItems);
      rightListRef.current?.handleChangeItems(rightItems);
    }
  };

  const handleCheckedLeft = (group: string, item: ValueLabel) => {
    item.enableDeleteButton = true;
    let leftItems = leftListRef.current?.items ?? [];
    const rightItems = rightListRef.current?.items ?? [];

    const leftGroupIndex = leftItems.findIndex((el) => el.group === group);
    leftItems[leftGroupIndex].items = leftItems[leftGroupIndex]?.items.filter(
      (i) => i.value !== item.value
    );

    const rightGroupIndex = rightItems?.findIndex((i) => i.group == group);
    if (rightGroupIndex > -1) {
      rightItems[rightGroupIndex].items = [
        ...rightItems[rightGroupIndex].items,
        item,
      ];
    } else {
      rightItems.push({
        group: group,
        items: [item],
      });
    }

    leftListRef.current?.handleChangeItems(leftItems);
    rightListRef.current?.handleChangeItems(rightItems);
  };

  useImperativeHandle(ref, () => ({
    leftListRef,
    rightListRef,
  }));

  return (
    <Grid container gap={2} justifyContent="flex-start">
      <Grid item xs={5} className="transfer-list-container">
        <VirtualizedCollapsibeList
          ref={leftListRef}
          initialItems={[]}
          handleCheckItem={handleCheckedLeft}
          alarmManagementFormRulesRef={alarmManagementFormRulesRef}
        />
      </Grid>
      <Grid item xs={5} className="transfer-list-container">
        <VirtualizedCollapsibeList
          ref={rightListRef}
          initialItems={[]}
          handleCheckItem={handleCheckedRight}
          alarmManagementFormRulesRef={alarmManagementFormRulesRef}
        />
      </Grid>
    </Grid>
  );
};

export default forwardRef<Ref, Props>(VirtualizedTransferList);
